import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useTimeout } from '@ulta/core/hooks/useTimeout/useTimeout';
import { useAppConfigContext } from '@ulta/core/providers/AppConfigProvider/AppConfigProvider';
import { hasItems } from '@ulta/core/utils/array/array';
import { handleEmptyObjects } from '@ulta/core/utils/handleEmptyObjects/handleEmptyObjects';
import { handleReplace } from '@ulta/core/utils/handleLocation/handleLocation';
import { scrollToElement } from '@ulta/core/utils/scrollToElement/scrollToElement';

import { useStickToHeader } from '@ulta/hooks/useStickToHeader/useStickToHeader';

import * as utils from './useAnchorList';

export const useAnchorList = ()=>{
  const [selectedLetter, setSelectedLetter] = useState();
  const [scrollTarget, setScrollTarget] = useState( '' );
  const [init, setInit] = useState( false );
  const { previewDate } = useAppConfigContext();
  const [scrollTimeout] = useTimeout();
  const [reportItemScrolling, setReportItemScrolling] = useState( true );
  // Ref for an element we place before the anchor list for tracking start position
  const positionElementRef = useRef();
  // Ref for the anchor list div
  const offsetElementRef = useRef( null );
  const [searchCount, setSearchCount] = useState( 0 );

  // use effect to scroll target
  useEffect( ()=>{
    const target = utils.getScrollTargetEl( { id: scrollTarget } );
    if( !target ){
      return;
    }
    scrollToElement( { el: target, offset: utils.getAnchorListHeight( { ref: offsetElementRef } ) } );
  }, [scrollTarget] );


  // Update hash based on selectedLetter and scroll
  useEffect( () => {
    if( !selectedLetter ){
      return;
    }
    utils.updateHistory( { letter: selectedLetter, previewDate } );
  }, [selectedLetter] );

  // Set letter based on hash on load only, if not A we will set a scrollTarget
  useEffect( () => {
    const hasLetter = utils.setLetterFromHash( { setSelectedLetter, setScrollTarget, scrollTimeout } );
    utils.handlePauseOnLoad( { hasLetter }, { setReportItemScrolling, setInit, setScrollTarget } );
  }, [] );


  // Watch searchCount to disable/enable item section scroll reporting
  useEffect( () => {
    utils.handleItemScrollReporting( { init, searchCount }, { setReportItemScrolling } );
  }, [searchCount, init] );

  // Custom Hook call for sticky position interaction w/ header's hide/show animation
  const [shouldOffsetElement, headerOffset] = useStickToHeader( { positionElementRef } );
  // for scroll
  const onItemScrolled = useCallback( utils.getItemScrolledCallback( { setSelectedLetter } ), [] );

  return { init, reportItemScrolling, shouldOffsetElement, headerOffset, onItemScrolled, positionElementRef, offsetElementRef,
    setScrollTarget, setSelectedLetter, setReportItemScrolling, selectedLetter, setSearchCount };

};


/**
 * Returns height of a React ref
 * @method getAnchorListHeight
 * @param {object} data - Data arguments
 * @param {object} data.ref React ref
 */
export const getAnchorListHeight = ( data ) => {
  const { ref } = data || {};
  if( isNaN( ref?.current?.offsetHeight ) || ref?.current?.offsetHeight === 0 ){
    return 0;
  }

  return ref?.current?.offsetHeight * -1;
};

/**
 * Generate a on item scroll callback
 * @method getItemScrolledCallback
 * @param {object} methods - Methods arguments
 * @param {function} methods.setSelectedLetter useState set selected letter
 * @returns {function} useItemScrolled callback
 */

let itemScrollTimeout;
export const getItemScrolledCallback = ( methods ) => {
  const { setSelectedLetter } = methods || {};
  if( !setSelectedLetter ){
    return;
  }
  const onItemScrolled = ( letter ) => {
    // This is a debounce pattern that prevents the scroll handler from firing too frequently.
    // TODO: This should be replaced with a useDebounce hook once available.
    global.clearTimeout( itemScrollTimeout );
    itemScrollTimeout = global.setTimeout( () => {
      global.requestAnimationFrame( () => {
        setSelectedLetter && setSelectedLetter( letter );
      } );
    }, 10 );
  };

  return onItemScrolled;
};

/**
 * Returns dom element for passed ID
 * @method getScrollTargetEl
 * @param {object} data - Data arguments
 * @param {string} data.id CSS id selector
 * @returns {object}  DOM element
 */
export const getScrollTargetEl = ( data ) => {
  const { id } = data || {};
  return id ? document.querySelector( id ) : null;
};

/**
 * Sets letter and scrolls id not A
 * @method setLetterFromHash
 * @param {object} methods - Methods arguments
 * @param {*} methods.setSelectedLetter - React useState setter for letter
 * @param {*} methods.setScrollTarget - React useState setter for scroll target
 */
export const setLetterFromHash = ( methods ) => {
  const { setSelectedLetter, setScrollTarget, scrollTimeout } = methods || {};
  if( !setSelectedLetter || !setScrollTarget || !scrollTimeout ){
    return;
  }
  const letter = utils.getLetterFromHash();
  setSelectedLetter( letter );
  if( letter === 'A' ){
    return false;
  }

  scrollTimeout( () => {
    setScrollTarget( utils.getSectionId( { letter } ) );
  }, 1000 );
  return true;
};

/**
 * Returns current letter from the URL
 * @method getLetterFromHash
 *
 * @returns {string} current letter
 */
export const getLetterFromHash = () => {
  if( typeof window === 'undefined' || !window.location?.hash ){
    return 'A';
  }

  const { hash } = window.location;
  if( hash === '#hash' ){
    return '#';
  }

  const letter = window.location.hash.replace( /[^A-Z]*/gi, '' );
  return letter.length === 1 ? letter.toUpperCase() : 'A';
};

/**
 * Handles if we should pause on load
 * @method handlePauseOnLoad
 * @param {object} data - Data arguments
 * @param {boolean} data.hasLetter - is not A
 * @param {object} methods - Methods arguments
 * @param {function} methods.setReportItemScrolling - call on item scroll
 * @param {function} methods.setInit - set hasLoaded
 * @param {function} methods.setScrollTarget - React useState setter for scroll target
 */
export const handlePauseOnLoad = ( data, methods ) => {
  const { hasLetter } = data || {};
  const { setReportItemScrolling, setInit, setScrollTarget } = methods || {};
  if( !setReportItemScrolling || !setInit || !setScrollTarget ){
    return;
  }
  if( !hasLetter ){
    setInit( true );
    return false;
  }

  utils.pauseItemScrollReporting( { delay: SCROLL_REPORTING_ONLOAD_DELAY }, {
    setReportItemScrolling,
    callback: () => {
      setInit( true );
      setScrollTarget( '' );
    }
  } );

  return true;
};

/**
 * Appends/replaces the URL with a hash - debounced/delayed by 500ms
 * @method updateHistory
 * @param {object} data - Data arguments
 * @param {string} data.letter - Input letter
 */
let hashTimeout;
export const updateHistory = ( data ) => {
  const { letter, previewDate } = data || {};
  if( !letter ){
    return;
  }
  const hash = utils.getHash( { letter } );
  // This is a debounce pattern that prevents the URL hash from updating on every event.
  // TODO: This should be replaced with a useDebounce hook once available.
  global.clearTimeout( hashTimeout );


  hashTimeout = global.setTimeout( () => {
    handleReplace( { url: hash, previewDate } );
  }, HASH_UPDATE_DELAY );
};

/**
 * Returns a hash to update the url with
 * @method getHash
 * @param {object} data - Data arguments
 * @param {string} data.letter - Input letter
 * @returns {string} A hash
 */
export const getHash = ( data ) => {
  const { letter } = data || {};
  if( !letter ){
    return;
  }
  return letter === '#' ? `#hash` : `#${letter}`;
} ;

/**
 * Delay in ms to wait before underlining letters after page load.
 * @constant {number}
 * @default
 */
export const SCROLL_REPORTING_ONLOAD_DELAY = 2000;
/**
 * We debounce for this amount of ms before updating the URL with currently scrolled to hash
 * @constant {number}
 * @default
 */
export const HASH_UPDATE_DELAY = 500;
/**
 * Pauses the items section from reporting scroll events while we're jumping manually
 * @method pauseItemScrollReporting
 * @param {object} data - Data arguments
 * @param {number} data.delay - timmer value
 * @param {object} methods - Methods arguments
 * @param {function} methods.setReportItemScrolling - call on item scroll
 * @param {function} methods.callback - onEnd
 */
let reportTimeout;
export const pauseItemScrollReporting = ( data, methods ) => {
  const { delay } = data || {};
  const { setReportItemScrolling, callback } = methods || {};
  if( !setReportItemScrolling || !callback ){
    return;
  }
  setReportItemScrolling( false );
  // This is a debounce pattern that creates a temporary pause for scroll reporting.
  // TODO: This should be replaced with a useDebounce hook once available.
  global.clearTimeout( reportTimeout );
  reportTimeout = global.setTimeout( () => {
    setReportItemScrolling( true );
    callback && callback();
  }, delay );
};

/**
 * Returns a CSS selector used for scrolling
 * @method getSectionId
 * @param {object} data - Data arguments
 * @param {string} data.letter - Input letter
 * @returns {string} - A CSS selector used to scroll/highlight the current hash
 */
export const getSectionId = ( data ) => {
  const { letter } = data || {};
  if( !letter ){
    return;
  }
  return letter === '#' ? `#Hashsection` : `#${letter}section`;
} ;

/**
 * Handles the state of item scroll reporting
 * @method handleItemScrollReporting
 * @param {object} data - Data arguments
 * @param {object} data.init - hasLoaded
 * @param {object} data.searchCount - # of searches
 * @param {object} methods - Methods arguments
 * @param {function} methods.setReportItemScrolling - call on item scroll
 */
export const handleItemScrollReporting = ( data, methods ) => {
  const { init, searchCount } = data || {};
  const { setReportItemScrolling } = methods || {};

  if( !init || !setReportItemScrolling ){
    return;
  }

  setReportItemScrolling( searchCount === 0 );
};






/**
 * Handles on letter click
 * @method getAnchorLetterClick
 * @param {object} data - Data arguments
 * @param {array} data.navItems - List of All items to display
 * @param {object} methods - Methods arguments
 * @param {function} methods.setSearchCount - Set search count
 * @param {function} methods.setFilteredItem - set Filtered item
 * @param {function} methods.setSearchInput - set Searc hInput
 * @param {function} methods.setScrollTarget - onBlur callback
 * @param {function} methods.setSelectedLetter - set Selected Letter
 * @param {function} methods.setReportItemScrolling - call on item scroll
 * @returns {function} onAnchorLetterClick callback
 */
export const getAnchorLetterClick = ( data, methods ) => {
  const { navItems } = data || {};
  const {
    setSearchCount,
    setFilteredItem,
    setSearchInput,
    setScrollTarget,
    setSelectedLetter,
    setReportItemScrolling
  } = methods || {};

  const onAnchorLetterClick = ( letter ) => {
    utils.resetFilterStateHandler(
      { letter, navItems },
      { setSearchCount, setFilteredItem, setSearchInput, setScrollTarget, setSelectedLetter }
    );

    setScrollTarget( utils.getSectionId( { letter } ) );

    utils.pauseItemScrollReporting( { delay: SCROLL_REPORTING_ONCLICK_DELAY }, {
      setReportItemScrolling,
      callback: () => setScrollTarget( '' )
    } );
  };

  return onAnchorLetterClick;
};

/**
 * Delay in ms to wait before underlining letters while scrolling.
 * Used to not underline letters while we programatically scroll.
 * @constant {number}
 * @default
 */
export const SCROLL_REPORTING_ONCLICK_DELAY = 850;

// Called by input clear and on letter click to reset the state
/**
 * Handles Reset Filter State for input
 * @method resetFilterStateHandler
 * @param {object} data - Data arguments
 * @param {object} data.letter - Input
 * @param {array} data.navItems - List of All Items to display
 * @param {object} methods - Methods arguments
 * @param {function} methods.setSearchCount - Set search count
 * @param {function} methods.setFilteredItem - set Filtered Items
 * @param {function} methods.setSearchInput - set Searc hInput
 * @param {function} methods.setScrollTarget - onBlur callback
 * @param {function} methods.setSelectedLetter - set Selected Letter
 */
export const resetFilterStateHandler = ( data, methods ) => {
  const { letter, navItems } = data || {};
  const { setSearchCount, setFilteredItem, setSearchInput, setScrollTarget, setSelectedLetter } = methods || {};

  if(
    !letter ||
    !navItems ||
    !setSearchCount ||
    !setFilteredItem ||
    !setSearchInput ||
    !setScrollTarget ||
    !setSelectedLetter
  ){
    return;
  }
  setSearchCount( 0 );
  setFilteredItem( [...navItems] );
  setSearchInput( '' );
  setScrollTarget( '' );
  setSelectedLetter( letter );
};


/**
 * Handles Items filter
 * @method filterItemsUtilityHandler
 * @param {object} data - Data arguments
 * @param {object} data.userInput - Input
 * @param {array} data.itemList - List of All Items
 * @param {array} data.navItems - List of All Items to display
 * @param {object} methods - Methods arguments
 * @param {function} methods.setSearchCount - Set search count
 * @param {function} methods.setFilteredItem - set Filtered Item
 * @param {function} methods.setSelectedLetter - set Selected Letter
 */
export const filterItemsUtilityHandler = ( data, methods ) => {
  const { brandList, userInput, navItems } = data || {};
  const { setSelectedLetter, setSearchCount, setFilteredItem } = methods || {};
  if( !brandList || !userInput || !navItems || !setSelectedLetter || !setSearchCount || !setFilteredItem ){
    return;
  }

  let searchCount = 0;
  // Clone each item array as [...] will perform a shallow copy and
  // this is more performant than JSON.stringify/parse
  const brandListModel = navItems.map( ( list ) => ( {
    ...list,
    items: [...list.items]
  } ) );
  brandList.forEach( ( brandsList, i ) => {
    const filteredResult = [];
    brandsList?.items?.forEach( ( item ) => {
      if( item.action?.label?.toLowerCase().includes( userInput?.toLowerCase() ) ){
        filteredResult.push( item );
        searchCount++;
      }
    } );
    brandListModel[i].items = filteredResult;
  } );
  setSelectedLetter( '' );
  setSearchCount( searchCount );
  setFilteredItem( brandListModel );
};



/**
 * Handles set focus
 * @method setFocusOnElementHandler
 * @param {object} data - Data arguments
 * @param {object} data.offsetElementRef - AnchorList Ref
 * @param {object} data.ref - ref shopallbrands
 */
export const setFocusOnElementHandler = ( data ) => {
  const { ref, offsetElementRef } = data || {};
  if( !ref || !offsetElementRef ){
    return;
  }
  utils.handleSkipLinkFocus( {
    el: ref.current,
    offsetElementRef
  } );
};


/**
 * @method handleSkipLinkFocus
 * @param {object} data - data arguments
 * @param {object} data.el - element data
 * @param {object} data.offsetElementRef - anchor list ref data
 */
export const handleSkipLinkFocus = ( data ) => {
  const { el, offsetElementRef } = data || {};
  scrollToElement( { el, offset: utils.getAnchorListHeight( { ref: offsetElementRef } ) } );
};

/**
 * to render items columnwise
 * @method ColumnSort
 * @param {*} items
 * @param {*} colRenderer
 * @returns {array} array of items divided in columns
 */
export const ColumnSort = ( data, methods )=>{
  const { items, breakpoint } = handleEmptyObjects( data );
  const { colRenderer, getNoOfColumns } = handleEmptyObjects( methods );
  const noOfColumns = getNoOfColumns && getNoOfColumns( breakpoint?.CURRENT_BREAKPOINT );
  const itemLength = Number( items?.items?.length ) > 0 ? items?.items?.length : 0;
  const itemCountPerColumn = itemLength > noOfColumns ? ~~( itemLength / noOfColumns ) + itemLength % noOfColumns : 1;
  if( !hasItems( items?.items ) ){
    return null;
  }

  const storeList = [];
  for ( let index = 0; index < noOfColumns; index++ ){
    const itemsPerColumn = items?.items?.slice( index * itemCountPerColumn, itemCountPerColumn * ( index + 1 ) );
    storeList.push( <ul key={ index }>{ colRenderer( itemsPerColumn, index ) }</ul> );
  }
  return storeList;
};